import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/useLanguage';
import { searchUsers } from '../../api/users';
import type { UserSearchResult } from '../../types/user';
import './UserSearch.css';

interface UserSearchProps {
  onSelectUser: (user: UserSearchResult) => void;
  placeholder?: string;
  minQueryLength?: number;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  onSelectUser,
  placeholder,
  minQueryLength = 2,
}) => {
  const { language, t } = useLanguage();
  const resolvedPlaceholder = placeholder ?? t('messages.search.placeholder');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= minQueryLength) {
        performSearch(query.trim());
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [query, minQueryLength]);

  const performSearch = async (searchQuery: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await searchUsers(searchQuery, 0, 20);
      setResults(response.content);
      setShowResults(true);
    } catch (err: any) {
      setError(err.response?.data?.message || t('messages.search.failed'));
      setResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectUser = (user: UserSearchResult) => {
    onSelectUser(user);
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  const handleBlur = () => {
    // Delay hiding results to allow click events
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="user-search">
      <div className="user-search-input-wrapper">
        <input
          type="text"
          className="user-search-input"
          placeholder={resolvedPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= minQueryLength && setShowResults(true)}
          onBlur={handleBlur}
          disabled={isLoading}
        />
        {isLoading && <span className="user-search-spinner">{t('common.loading')}</span>}
      </div>

      {error && (
        <div className="user-search-error">{error}</div>
      )}

      {showResults && results.length > 0 && (
        <div className="user-search-results">
          {results.map((user) => (
            <div
              key={user.id}
              className="user-search-result-item"
              onClick={() => handleSelectUser(user)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
            >
              <div className="user-search-result-username">{user.username}</div>
              {user.lastActiveAt && (
                <div className="user-search-result-meta">
                  {t('messages.search.activeLabel')}{' '}
                  {new Date(user.lastActiveAt).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showResults && !isLoading && query.trim().length >= minQueryLength && results.length === 0 && (
        <div className="user-search-no-results">
          {t('messages.search.noResults').replace('{query}', query)}
        </div>
      )}
    </div>
  );
};
